/**
 * PRP API - Psychiatric Rehabilitation Program (Maryland PRP) workspace.
 * Backend: ehr-go/internal/modules/prp/
 *
 * Routes:
 *   GET  /prp/workflows              signing order per document type
 *   GET  /prp/caseload?mine=true     active PRP enrollments
 *   GET  /prp/enrollments?patient_id=   POST /prp/enrollments (admit)
 *   PUT  /prp-enrollments/:id           POST /prp-enrollments/:id/discharge
 *   GET  /prp/authorizations            POST /prp/authorizations
 *   PUT  /prp-authorizations/:id
 *   GET  /prp/work-queue?mine=true   draft / pending / signed counts
 *   GET  /prp-documents              list (patient_id, doc_type, status, mine)
 *   POST /prp-documents              create draft
 *   GET  /prp-documents/:id
 *   PUT  /prp-documents/:id          save draft content
 *   POST /prp-documents/:id/sign
 *   POST /prp-documents/:id/finish          validate + lock for signatures
 *   POST /prp-documents/:id/refresh-client  referral: re-read client info
 *   GET  /prp-documents/:id/pdf             printable referral
 *   GET|POST /prp-documents/:id/sign-links  secure links (outside provider, or the client)
 *   POST /prp-documents/:id/return   send back to draft
 *   POST /prp-documents/:id/void
 *   POST /prp-documents/:id/addenda
 */
import apiClient, { apiGet, apiGetRaw, apiPostRaw, apiPut, apiPutRaw } from "./client";

export type PrpDocType =
  | "referral"
  | "dla20"
  | "presenting_problems"
  | "irp"
  | "contact_note"
  | "monthly_summary"
  | "discharge_summary";

export type PrpDocStatus = "draft" | "pending_signatures" | "signed" | "voided";

export type PrpSignMethod = "e_sign" | "ink" | "verbal" | "refused" | "secure_link";

export const PRP_DOC_LABELS: Record<PrpDocType, string> = {
  referral: "Referral",
  dla20: "DLA-20 Assessment",
  presenting_problems: "Presenting Problems",
  irp: "IRP",
  contact_note: "Contact Note",
  monthly_summary: "Monthly Summary",
  discharge_summary: "Discharge Summary",
};

export const PRP_STATUS_LABELS: Record<PrpDocStatus, string> = {
  draft: "Draft",
  pending_signatures: "Awaiting signatures",
  signed: "Signed",
  voided: "Voided",
};

export interface PrpWorkflowStep {
  key: string;
  label: string;
  staff_roles?: string[];
  required: boolean;
  allow_verbal: boolean;
  allow_refusal: boolean;
  must_be_author: boolean;
  must_differ_from?: string;
}

export interface PrpSignature {
  id: string;
  step: number;
  role: string;
  signer_user_id?: string;
  signer_name?: string;
  credentials?: string;
  method: PrpSignMethod;
  reason?: string;
  captured_by?: string;
  signed_at: string;
  evidence_hash: string;
  invalidated_at?: string;
  invalidated_reason?: string;
}

export interface PrpStepStatus extends PrpWorkflowStep {
  signed: boolean;
  signature?: PrpSignature;
}

export interface PrpAddendum {
  id: string;
  author_id: string;
  body: string;
  created_at: string;
}

export interface PrpDocument {
  id: string;
  organization_id: string;
  patient_id: string;
  episode_id?: string;
  /** Started while the client was in PRP-M: the parent / guardian signature is required. */
  for_minor?: boolean;
  /** Contact notes started from the calendar. */
  appointment_id?: string;
  group_session_id?: string;
  doc_type: PrpDocType;
  status: PrpDocStatus;
  document_date: string;
  content?: string;
  version: number;
  author_id: string;
  locked_at?: string;
  voided_at?: string;
  void_reason?: string;
  signatures?: PrpSignature[];
  addenda?: PrpAddendum[];
  steps: PrpStepStatus[];
  next_step?: string;
  /** List responses only: a one-line description (content is omitted). */
  summary?: PrpDocumentSummary;
  created_at: string;
  updated_at: string;
}

export interface PrpCaseloadEntry {
  enrollment_id: string;
  patient_id: string;
  counselor_user_id: string;
  program: "adult" | "minor";
  admission_date: string;
  status: string;
  draft_count: number;
  pending_signature_count: number;
  last_contact_date?: string;
  last_irp_date?: string;
  auth_end_date?: string;
  auth_status?: PrpAuthStatus;
}

export type PrpAuthStatus = "requested" | "approved" | "denied";

/**
 * Maryland PRP service levels (COMAR 10.21.25.09 B, single-PRP tiers). The
 * minimum is the number of countable services needed each month.
 */
export const PRP_SERVICE_LEVELS = [
  { key: "community_support", label: "Community support (lives with family)", minimum: 3 },
  { key: "supported_living", label: "Supported living (lives independently)", minimum: 6 },
  { key: "transition", label: "Transition from inpatient care", minimum: 4 },
] as const;

export type PrpServiceLevel = "" | (typeof PRP_SERVICE_LEVELS)[number]["key"];

/** One active client's countable contacts this month (GET /prp/contact-counts). */
export interface PrpClientContactCount {
  patient_id: string;
  enrollment_id: string;
  counselor_user_id: string;
  program: "adult" | "minor";
  service_level: PrpServiceLevel;
  /** 0 when the service level is not set. */
  minimum: number;
  counted: number;
  signed_notes: number;
  unsigned_notes: number;
  /** Signed notes that did not count, by reason. */
  skipped: Partial<Record<PrpContactSkipReason, number>>;
  last_contact?: string;
}

export type PrpContactSkipReason =
  | "on_site_under_60_min"
  | "off_site_under_15_min"
  | "second_same_day"
  | "over_30_this_month";

export interface PrpContactCounts {
  month: string;
  /** Days left in the month, today included (0 for past months). */
  days_left: number;
  clients: PrpClientContactCount[];
}

export function getPrpContactCounts(params: { month?: string; mine?: boolean }): Promise<PrpContactCounts> {
  return apiGet("/prp/contact-counts", {
    params: { month: params.month, mine: params.mine ? true : undefined },
  });
}

/** Clinic PRP settings (Settings → PRP Program). */
export interface PrpProgramSettings {
  program_name: string;
  license_number: string;
  npi: string;
  aso_name: string;
  programs_offered: ("adult" | "minor")[];
  default_service_level: PrpServiceLevel;
  irp_review_days: number;
  auth_warning_days: number;
  updated_at?: string;
  updated_by?: string;
}

/** Registers a client from the PRP workspace; the referral comes next. */
export function createPrpClient(req: {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender?: "male" | "female" | "other" | "unknown";
  phone?: string;
}): Promise<{ id: string; mrn: string; first_name: string; last_name: string; date_of_birth: string }> {
  return apiPostRaw<{ data: { id: string; mrn: string; first_name: string; last_name: string; date_of_birth: string } }>(
    "/prp/clients",
    req,
  ).then((r) => r.data);
}

export type PrpComplianceSeverity = "high" | "warn";

export interface PrpComplianceIssue {
  code: string;
  severity: PrpComplianceSeverity;
  message: string;
  document_id?: string;
}

export interface PrpClientCompliance {
  patient_id: string;
  enrollment_id: string;
  counselor_user_id: string;
  issues: PrpComplianceIssue[];
}

export interface PrpComplianceFinding {
  code: string;
  severity: PrpComplianceSeverity;
  message: string;
  date?: string;
  author_id?: string;
  document_ids: string[];
  patient_ids: string[];
}

export interface PrpComplianceReport {
  generated_at: string;
  clients: PrpClientCompliance[];
  findings: PrpComplianceFinding[];
}

export function getPrpCompliance(mine = false): Promise<PrpComplianceReport> {
  return apiGet("/prp/compliance", { params: mine ? { mine: true } : undefined });
}

export function getPrpSettings(): Promise<PrpProgramSettings> {
  return apiGet("/prp/settings");
}

export function savePrpSettings(req: PrpProgramSettings): Promise<PrpProgramSettings> {
  return apiPut("/prp/settings", req);
}

export interface PrpEnrollment {
  id: string;
  patient_id: string;
  referral_document_id?: string;
  counselor_user_id: string;
  program: "adult" | "minor";
  /** Fee-schedule tier that sets the monthly contact minimum ("" = not set). */
  service_level?: PrpServiceLevel;
  admission_date: string;
  status: "active" | "discharged";
  discharge_date?: string;
  discharge_reason?: string;
  created_at: string;
}

export interface PrpAuthorization {
  id: string;
  patient_id: string;
  enrollment_id: string;
  auth_number?: string;
  payer: string;
  start_date: string;
  end_date: string;
  units_authorized?: number;
  /** Approved authorizations with units: months used so far (computed by the server). */
  units_used?: number;
  units_used_months?: string[];
  status: PrpAuthStatus;
  notes?: string;
  created_at: string;
}

export interface PrpAuthorizationRequest {
  enrollment_id?: string;
  auth_number: string;
  payer: string;
  start_date: string;
  end_date: string;
  units_authorized?: number | null;
  status: PrpAuthStatus;
  notes?: string;
}

// ── Referral form (Maryland PRP referral) ──────────────────────────────────

export const PRP_IMPAIRMENTS: { key: string; label: string }[] = [
  {
    key: "employment",
    label: "Inability to establish/maintain independent competitive employment",
  },
  {
    key: "daily_living",
    label:
      "Inability to perform instrumental activities of daily living (meal prep, chores, shopping, money or medication management)",
  },
  { key: "support_system", label: "Inability to establish or maintain a personal support system" },
  { key: "concentration", label: "Frequent deficiencies of concentration, persistence or pace" },
  {
    key: "self_care",
    label:
      "Inability to perform or maintain self-care (hygiene, grooming, nutrition, medical care, personal safety)",
  },
  { key: "self_direction", label: "Deficiencies in self-direction" },
  {
    key: "financial_support",
    label: "Inability to procure financial assistance to support community living",
  },
];

export const PRP_MIN_IMPAIRMENTS = 3;

/**
 * Maryland adult PRP/RRP Priority Population diagnoses - the primary diagnosis
 * must be one of these (unless a waiver applies). Union of the DSM-5 attachment
 * and the newer payer list. Mirrors ehr-go/internal/modules/prp/priority_dx.go.
 */
export const PRP_PRIORITY_DIAGNOSES: { code: string; label: string }[] = [
  { code: "F20.0", label: "Paranoid schizophrenia" },
  { code: "F20.1", label: "Disorganized schizophrenia" },
  { code: "F20.2", label: "Catatonic schizophrenia" },
  { code: "F20.3", label: "Undifferentiated schizophrenia" },
  { code: "F20.5", label: "Residual schizophrenia" },
  { code: "F20.81", label: "Schizophreniform disorder" },
  { code: "F20.89", label: "Other schizophrenia" },
  { code: "F20.9", label: "Schizophrenia, unspecified" },
  { code: "F21", label: "Schizotypal personality disorder" },
  { code: "F22", label: "Delusional disorder" },
  { code: "F25.0", label: "Schizoaffective disorder, bipolar type" },
  { code: "F25.1", label: "Schizoaffective disorder, depressive type" },
  { code: "F25.8", label: "Other schizoaffective disorders" },
  { code: "F25.9", label: "Schizoaffective disorder, unspecified" },
  { code: "F28", label: "Other specified schizophrenia spectrum and other psychotic disorder" },
  { code: "F29", label: "Unspecified schizophrenia spectrum and other psychotic disorder" },
  { code: "F31.0", label: "Bipolar I disorder, current or most recent episode hypomanic" },
  { code: "F31.13", label: "Bipolar I disorder, current or most recent episode manic, severe without psychotic features" },
  { code: "F31.2", label: "Bipolar I disorder, current or most recent episode manic, severe with psychotic features" },
  { code: "F31.4", label: "Bipolar I disorder, current or most recent episode depressed, severe without psychotic features" },
  { code: "F31.5", label: "Bipolar I disorder, current or most recent episode depressed, severe with psychotic features" },
  { code: "F31.63", label: "Bipolar I disorder, mixed, severe without psychotic features" },
  { code: "F31.64", label: "Bipolar I disorder, mixed, severe with psychotic features" },
  { code: "F31.81", label: "Bipolar II disorder" },
  { code: "F31.9", label: "Bipolar I disorder, unspecified" },
  { code: "F33.2", label: "Major depressive disorder, recurrent, severe without psychotic features" },
  { code: "F33.3", label: "Major depressive disorder, recurrent, severe with psychotic features" },
  { code: "F60.3", label: "Borderline personality disorder" },
];

/** Co-occurring substance use disorders - secondary only, never primary. */
export const PRP_SUD_DIAGNOSES: { code: string; label: string }[] = [
  { code: "F10.10", label: "Alcohol use disorder, mild" },
  { code: "F10.20", label: "Alcohol use disorder, moderate/severe" },
  { code: "F11.10", label: "Opioid use disorder, mild" },
  { code: "F11.20", label: "Opioid use disorder, moderate/severe" },
  { code: "F12.10", label: "Cannabis use disorder, mild" },
  { code: "F12.20", label: "Cannabis use disorder, moderate/severe" },
  { code: "F14.10", label: "Cocaine use disorder, mild" },
  { code: "F14.20", label: "Cocaine use disorder, moderate/severe" },
  { code: "F15.10", label: "Amphetamine-type substance use disorder, mild" },
  { code: "F15.20", label: "Amphetamine-type substance use disorder, moderate/severe" },
  { code: "F17.200", label: "Tobacco use disorder, moderate/severe" },
  { code: "Z72.0", label: "Tobacco use disorder, mild" },
  { code: "F19.10", label: "Other (or unknown) substance use disorder, mild" },
  { code: "F19.20", label: "Other (or unknown) substance use disorder, moderate/severe" },
];

/** Either waiver lifts the priority-diagnosis requirement. */
export const PRP_DX_WAIVERS: { key: string; label: string }[] = [
  {
    key: "ncr_conditional_release",
    label:
      "Committed as not criminally responsible and conditionally released from a Mental Hygiene facility (Health-General Art., Title 12)",
  },
  {
    key: "mh_facility_6m",
    label:
      "In a Mental Hygiene facility more than 6 months and requires RRP services (excludes individuals eligible for DDA services)",
  },
];

const normalizeDx = (code: string) => code.trim().replace(/\./g, "").toUpperCase();
const PRIORITY_DX_SET = new Set(PRP_PRIORITY_DIAGNOSES.map((d) => normalizeDx(d.code)));

export function isPriorityDiagnosis(code: string): boolean {
  return PRIORITY_DX_SET.has(normalizeDx(code));
}

export const PRP_LENGTHS: { key: string; label: string }[] = [
  { key: "lt_1m", label: "< 1 month" },
  { key: "2_3m", label: "2–3 months" },
  { key: "4_6m", label: "4–6 months" },
  { key: "7_12m", label: "7–12 months" },
  { key: "12m_plus", label: "12 months or more" },
];

export const PRP_FREQUENCIES: { key: string; label: string }[] = [
  { key: "1x_week", label: "1x / week" },
  { key: "2x_week", label: "2x / week" },
  { key: "1x_month", label: "1x / month" },
  { key: "3x_month", label: "3x / month" },
  { key: "6x_month", label: "6x / month" },
];

/** Client block snapshotted from the chart when the referral is created. */
export interface PrpReferralClient {
  name: string;
  date_of_birth: string;
  /** Last four digits only - the full SSN never leaves the chart. */
  ssn_last4: string;
  race: string;
  gender: string;
  marital_status: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
}

export interface PrpReferralContent {
  referral_date: string;
  client: PrpReferralClient;
  referral_type: "" | "initial" | "concurrent";
  medicaid_number: string;
  requested_length: string;
  requested_frequency: string;
  impairments: string[];
  diagnoses: { code: string; description: string; primary: boolean }[];
  /** PRP_DX_WAIVERS key; lifts the priority-list rule on the primary. */
  dx_waiver?: string;
  diagnosed_by: string;
  diagnosis_date: string;
  medications: { name: string; dose: string }[];
  no_medication_reason: string;
  reasons_for_referral: string;
  referring_provider: {
    name: string;
    credentials: string;
    supervisor_name: string;
    agency: string;
    user_id?: string;
    supervisor_user_id?: string;
    /** Outside the organization - signs through an emailed secure link. */
    external?: boolean;
    email?: string;
    phone?: string;
    fax?: string;
  };
  guardian?: { name: string; phone: string; email: string; address: string };
}

export interface PrpDocumentSummary {
  headline?: string;
  detail?: string;
  start_time?: string;
  end_time?: string;
  minutes?: number;
}

export interface PrpWorkQueue {
  drafts: number;
  pending_signatures: number;
  signed_last_30_days: number;
}

export interface PrpDocumentFilter {
  patient_id?: string;
  episode_id?: string;
  doc_type?: PrpDocType;
  status?: PrpDocStatus;
  mine?: boolean;
  /** Documents whose next signature is the caller's (GET /prp/awaiting-me). */
  awaiting_me?: boolean;
  limit?: number;
  offset?: number;
}

export interface CreatePrpDocumentRequest {
  patient_id: string;
  doc_type: PrpDocType;
  document_date: string;
  content?: Record<string, unknown>;
  /** Start from a signed document of the same type (IRP review). */
  copy_from_id?: string;
  /**
   * Contact note started from the calendar. If the client already has a live
   * note for this appointment / group session, that note is returned instead.
   */
  appointment_id?: string;
  group_session_id?: string;
}

export interface SignPrpDocumentRequest {
  step: string;
  method: PrpSignMethod;
  signer_name?: string;
  credentials?: string;
  signature_data?: string;
  reason?: string;
}

// ── Presenting Problems ────────────────────────────────────────────────────

export const PRP_DEFICIT_AREAS: { key: string; label: string }[] = [
  { key: "independent_living", label: "Independent living skills" },
  { key: "self_care", label: "Self-care skills" },
  { key: "financial_management", label: "Financial management skills" },
  { key: "social_skills", label: "Social skills" },
  { key: "vocational", label: "Vocational / educational skills" },
  { key: "health_management", label: "Health and medication management" },
];

export interface PrpPPGoal {
  goal: string;
  progress: string;
  date_established: string;
  target_date: string;
  date_achieved: string;
  frequency: string;
  modality: string;
}

export interface PrpPresentingProblemsContent {
  diagnosis: string;
  diagnosis_duration: string;
  deficit_areas: string[];
  treatment_expectations: string;
  strengths: string;
  weaknesses: string;
  additional_info: string;
  client_needs: string;
  client_abilities: string;
  client_preferences: string;
  long_term_goals: PrpPPGoal[];
  short_term_goals: PrpPPGoal[];
  discharge_plan: string;
  discharge_date: string;
  discharge_supports: string[];
  /** Program psychiatrist who signs; only they can sign that step. */
  psychiatrist_user_id?: string;
  psychiatrist_name?: string;
  /** Outside (contracted) psychiatrist: no login, signs by emailed link. */
  psychiatrist_external?: boolean;
  psychiatrist_email?: string;
  /** Clinician who completed it with the client (defaults to the assigned counselor). */
  clinician_user_id?: string;
  clinician_name?: string;
}

// ── IRP ────────────────────────────────────────────────────────────────────

export const PRP_IRP_DOMAINS: { key: string; label: string }[] = [
  { key: "mental_health", label: "Mental health / substance use" },
  { key: "independent_living", label: "Independent living skills" },
  { key: "entitlement", label: "Entitlement assistance" },
  { key: "physical_health", label: "Physical health and wellness" },
  { key: "education", label: "Education" },
  { key: "vocational", label: "Vocational / employment" },
  { key: "social_skills", label: "Social skills" },
  { key: "housing", label: "Housing" },
  { key: "crisis_support", label: "Crisis support" },
  { key: "community_integration", label: "Community integration" },
];

export type PrpGoalStatus = "new" | "continue" | "revised" | "met" | "discontinued";

export const PRP_GOAL_STATUSES: { key: PrpGoalStatus; label: string }[] = [
  { key: "new", label: "New" },
  { key: "continue", label: "Continue" },
  { key: "revised", label: "Revised" },
  { key: "met", label: "Met" },
  { key: "discontinued", label: "Discontinued" },
];

export interface PrpIrpGoal {
  id: string;
  domain: string;
  status: PrpGoalStatus;
  client_words: string;
  clinical_goal: string;
  target_date: string;
  short_term_goal: string;
  interventions: { client: string[]; rc: string[]; therapist: string[] };
  why: string;
  when: string;
  review_assessment: string;
  intervention_changed: boolean | null;
}

export interface PrpIrpContent {
  irp_type: "" | "initial" | "review";
  previous_irp_id: string;
  site: string;
  strengths_abilities: string;
  needs_preferences: string;
  recovery_expectations: string;
  integration_inclusion: string;
  sud: {
    has_challenge: boolean | null;
    referral_made: boolean | null;
    plan_if_no_referral: string;
    ongoing_communication: boolean | null;
    no_communication_reason: string;
    feedback_from: string;
    feedback_summary: string;
  };
  goals: PrpIrpGoal[];
  services: {
    service: string;
    times: string;
    duration: string;
    referred_out: boolean;
    specify: string;
  }[];
  external_supports: {
    service: string;
    currently_from: string;
    referred_to: string;
    description: string;
  }[];
  transition_plan: { target_date: string; objectives_met: boolean | null; condition: string };
  discharge_criteria: { target_date: string; objectives_met: boolean | null; condition: string };
  diagnoses: { code: string; description: string; primary: boolean }[];
  diagnosis_changed: boolean | null;
  participation_level: string;
  housing_level: string;
  how_treatment_supports: string;
  copy_received: "" | "received" | "declined";
  safety_plan: { needed: boolean | null; reason: string };
}

// ── Contact note ───────────────────────────────────────────────────────────

export type PrpContactSetting = "" | "onsite" | "offsite" | "telehealth";

export const PRP_PARTICIPATION: { key: string; label: string }[] = [
  { key: "excellent", label: "Excellent" },
  { key: "good", label: "Good" },
  { key: "average", label: "Average" },
  { key: "minimal", label: "Minimal" },
];

export const PRP_MODES_OF_SERVICE: { key: string; label: string }[] = [
  { key: "one_on_one", label: "One on one" },
  { key: "treatment_team", label: "Treatment team meeting" },
  { key: "care_coordination", label: "Care coordination" },
  { key: "discharge_planning", label: "Discharge planning" },
  { key: "prp_group", label: "PRP group" },
  { key: "in_home_family", label: "In-home services / family" },
  { key: "case_management", label: "Case management / referral" },
];

export const PRP_INTERVENTION_TYPES: { key: string; label: string }[] = [
  { key: "resource_development", label: "Resource development / acquisition" },
  { key: "community_support", label: "Community / social support" },
  { key: "vocational_support", label: "Vocational / employment support" },
  { key: "crisis_support", label: "Crisis support" },
  { key: "skill_development", label: "Skill development" },
  { key: "psychoeducation", label: "Psychoeducation / increase knowledge" },
  { key: "transportation", label: "Transportation" },
];

export const PRP_PROGRESS_SCALE: { value: number; label: string }[] = [
  { value: 1, label: "No progress" },
  { value: 2, label: "Minimal" },
  { value: 3, label: "Moderate" },
  { value: 4, label: "Significant" },
  { value: 5, label: "Goal achieved" },
];

export interface PrpContactNoteGoal {
  goal_id: string;
  label: string;
  domain: string;
  progress: number;
  progress_text: string;
}

export interface PrpContactNoteContent {
  start_time: string;
  end_time: string;
  setting: PrpContactSetting;
  telehealth_method: "" | "video" | "audio";
  location: string;
  reason: string;
  interventions: string;
  participation: string;
  irp_id: string;
  goals: PrpContactNoteGoal[];
  mode_of_service: string[];
  domains: string[];
  intervention_types: string[];
  transportation_to: string;
}

export function getPrpDocumentChecks(id: string): Promise<{ warnings: string[] }> {
  return apiGet(`/prp-documents/${id}/checks`);
}

// ── DLA-20 (adult) ─────────────────────────────────────────────────────────
// Domain list and scoring rules only; MTM's rubric anchor text is copyrighted
// and not reproduced - hints below are our own short descriptions.

export const DLA20_DOMAINS: { key: string; name: string; hint: string }[] = [
  {
    key: "health_practices",
    name: "Health practices",
    hint: "Mood, medications, physical and mental health self-care",
  },
  {
    key: "housing",
    name: "Housing stability",
    hint: "Living situation, stability, household upkeep",
  },
  {
    key: "communication",
    name: "Communication",
    hint: "Expressing needs, listening, verbal and non-verbal",
  },
  { key: "safety", name: "Safety", hint: "Suicidality, homicidality, safe everyday decisions" },
  {
    key: "managing_time",
    name: "Managing time",
    hint: "Sleep, daily routine, keeping appointments",
  },
  { key: "managing_money", name: "Managing money", hint: "Income, bills, spending" },
  { key: "nutrition", name: "Nutrition", hint: "Regular meals, food access, shopping and cooking" },
  {
    key: "problem_solving",
    name: "Problem solving",
    hint: "Handling daily problems and decisions",
  },
  {
    key: "family_relationships",
    name: "Family relationships",
    hint: "Family and significant others",
  },
  {
    key: "alcohol_drug",
    name: "Alcohol / drug use",
    hint: "Alcohol, drugs, tobacco, medication misuse",
  },
  { key: "leisure", name: "Leisure", hint: "Hobbies and enjoyable activities" },
  {
    key: "community_resources",
    name: "Community resources",
    hint: "Transportation, services, church, food pantry",
  },
  { key: "social_network", name: "Social network", hint: "Friends, neighbors, peers" },
  { key: "sexual_health", name: "Sexual health", hint: "Safe and appropriate sexual health" },
  { key: "productivity", name: "Productivity", hint: "Work, school, volunteering, homemaking" },
  {
    key: "coping_skills",
    name: "Coping skills",
    hint: "Understanding the illness, coping strategies",
  },
  {
    key: "behavioral_norms",
    name: "Behavioral norms",
    hint: "Legal issues, conduct in the community",
  },
  { key: "personal_hygiene", name: "Personal hygiene", hint: "Bathing, oral care" },
  { key: "grooming", name: "Grooming", hint: "Hair, nails, shaving" },
  { key: "dress", name: "Dress", hint: "Clean, weather-appropriate clothing" },
];

export const DLA20_SCALE: { value: number; label: string }[] = [
  { value: 1, label: "Extreme - pervasive support" },
  { value: 2, label: "Severe - extensive support" },
  { value: 3, label: "Serious - intensive support" },
  { value: 4, label: "Moderate - frequent support" },
  { value: 5, label: "Mild - occasional support" },
  { value: 6, label: "Minimal - little support" },
  { value: 7, label: "Independent - no support" },
];

/** Screening facts (last 30 days) that force or cap a domain score. */
/**
 * Screening facts (last 30 days) → score rules. Mirrors dla20Rules in
 * ehr-go/internal/modules/prp/dla20.go. `floor`: when the fact is NOT checked
 * the score must be at least `value` (the fact is what allows a lower score).
 */
export const DLA20_FACTS: {
  key: string;
  question: string;
  domain: string;
  value: number;
  exact: boolean;
  rule: string;
  floor?: boolean;
}[] = [
  {
    key: "suicide_attempt_30d",
    question: "Suicide attempt in the last 30 days",
    domain: "safety",
    value: 1,
    exact: true,
    rule: "Safety must be 1",
  },
  {
    key: "suicidal_plan",
    question: "Has a suicide plan",
    domain: "safety",
    value: 2,
    exact: false,
    rule: "Safety at most 2",
  },
  {
    key: "suicidal_thoughts",
    question: "Suicidal thoughts in the last month",
    domain: "safety",
    value: 3,
    exact: false,
    rule: "Safety at most 3",
  },
  {
    key: "institution_over_6_days_30d",
    question: "More than 6 days in prison or hospital (last 30 days)",
    domain: "housing",
    value: 1,
    exact: true,
    rule: "Housing must be 1",
  },
  {
    key: "homeless_or_protective",
    question: "Homeless or in a 24/7 protective setting",
    domain: "housing",
    value: 2,
    exact: false,
    rule: "Housing at most 2",
  },
  {
    key: "group_home",
    question: "Lives in a group home",
    domain: "housing",
    value: 4,
    exact: false,
    rule: "Housing at most 4",
  },
  {
    key: "no_family_contact_30d",
    question: "No family / significant-other contact in 30 days",
    domain: "family_relationships",
    value: 4,
    exact: true,
    rule: "Family must be 4",
  },
  {
    key: "no_income",
    question: "No income or access to money from any source",
    domain: "managing_money",
    value: 1,
    exact: true,
    rule: "Managing money must be 1",
  },
  {
    key: "payee_poor_management",
    question: "Payee/guardian manages money and management is very poor",
    domain: "managing_money",
    value: 2,
    exact: true,
    rule: "Managing money must be 2",
  },
  {
    key: "eating_disorder_or_no_food",
    question: "Eating disorder or no reliable access to food",
    domain: "nutrition",
    value: 2,
    exact: false,
    rule: "Nutrition at most 2",
  },
  {
    key: "warrant_parole_dui_release",
    question: "Outstanding warrant, active parole, DUI, or recently released",
    domain: "behavioral_norms",
    value: 3,
    exact: false,
    rule: "Behavioral norms at most 3",
  },
  {
    key: "court_involvement",
    question: "Current court involvement or restrictions",
    domain: "behavioral_norms",
    value: 4,
    exact: false,
    rule: "Behavioral norms at most 4",
  },
  {
    key: "sexual_trauma_30d",
    question: "Sexual abuse, assault or identity distress in the last month",
    domain: "sexual_health",
    value: 3,
    exact: false,
    rule: "Sexual health at most 3",
  },
  {
    key: "partial_med_adherence",
    question: "Moderate mood problems, partial medication adherence, or medications not working",
    domain: "health_practices",
    value: 4,
    exact: false,
    rule: "Health practices at most 4",
  },
  {
    key: "not_taking_meds_serious_mood",
    question: "Serious mood problems and not taking medications as prescribed",
    domain: "health_practices",
    value: 3,
    exact: false,
    rule: "Health practices at most 3",
  },
  {
    key: "health_danger",
    question: "Health is in real danger",
    domain: "health_practices",
    value: 3,
    exact: false,
    floor: true,
    rule: "needed to score Health practices 1–2",
  },
  {
    key: "not_independent_housing",
    question: "Does not live independently in the community",
    domain: "housing",
    value: 4,
    exact: false,
    rule: "Housing at most 4",
  },
  {
    key: "incongruent_communication",
    question: "Incongruent messages reflecting serious symptoms",
    domain: "communication",
    value: 3,
    exact: false,
    rule: "Communication at most 3",
  },
  {
    key: "threatening_communication",
    question: "Severely threatening communication",
    domain: "communication",
    value: 2,
    exact: false,
    rule: "Communication at most 2",
  },
  {
    key: "sleep_5_to_7h",
    question: "Averages 5–7 hours of sleep a night",
    domain: "managing_time",
    value: 5,
    exact: false,
    rule: "Managing time at most 5",
  },
  {
    key: "sleep_5h_no_routine",
    question: "About 5 hours of sleep or less, with no sleep routine",
    domain: "managing_time",
    value: 4,
    exact: false,
    rule: "Managing time at most 4",
  },
  {
    key: "poor_diet",
    question: "Meals are mostly fast food, high-fat/sugar food, or caffeine/energy drinks",
    domain: "nutrition",
    value: 3,
    exact: false,
    rule: "Nutrition at most 3",
  },
  {
    key: "routine_decision_difficulty",
    question: "Routine difficulty making decisions when facing problems",
    domain: "problem_solving",
    value: 3,
    exact: false,
    rule: "Problem solving at most 3",
  },
  {
    key: "problem_solving_danger",
    question: "Poor problem solving endangers health",
    domain: "problem_solving",
    value: 2,
    exact: false,
    floor: true,
    rule: "needed to score Problem solving 1",
  },
  {
    key: "family_estranged",
    question: "Distressed, stressed or estranged from family",
    domain: "family_relationships",
    value: 3,
    exact: false,
    rule: "Family at most 3",
  },
  {
    key: "family_volatile",
    question: "High-risk, volatile family relationships",
    domain: "family_relationships",
    value: 2,
    exact: false,
    rule: "Family at most 2",
  },
  {
    key: "heavy_smoking",
    question: "Smokes more than 1.5 packs of cigarettes a day",
    domain: "alcohol_drug",
    value: 3,
    exact: false,
    rule: "Alcohol / drug use at most 3",
  },
  {
    key: "tobacco_plus_substance_untreated",
    question: "Tobacco plus alcohol or drugs, not in treatment",
    domain: "alcohol_drug",
    value: 2,
    exact: false,
    rule: "Alcohol / drug use at most 2",
  },
  {
    key: "limited_insight_coping",
    question: "Coping skills not used effectively, limited insight into the illness",
    domain: "coping_skills",
    value: 3,
    exact: false,
    rule: "Coping skills at most 3",
  },
  {
    key: "negative_coping",
    question: "Negative use of coping skills",
    domain: "coping_skills",
    value: 2,
    exact: false,
    rule: "Coping skills at most 2",
  },
];

export interface PrpDla20Content {
  version: "adult";
  assessment_type: "" | "intake" | "reassessment" | "discharge";
  previous_assessment_id: string;
  items: Record<string, { score: number; narrative: string }>;
  facts: Record<string, boolean>;
  substance_history: "" | "current" | "past" | "never";
  summary: string;
}

// ── Monthly summary ────────────────────────────────────────────────────────

export interface PrpMonthlySummaryContent {
  month: string;
  progress_services_needed: string;
  barriers: string;
  continued_justification: string;
  community_referrals: string;
  collaboration_summary: string;
  collaboration_dates: string[];
}

export interface PrpMonthlyRollup {
  month: string;
  contacts: number;
  total_minutes: number;
  by_setting: Record<string, number>;
  unsigned_notes: number;
  goals: {
    goal_id: string;
    label: string;
    domain: string;
    contacts: number;
    avg_progress: number;
    last_progress: number;
    last_note: string;
  }[];
  contact_dates: string[];
  intervention_use: Record<string, number>;
}

export interface PrpMonthlyAIDraft {
  progress_services_needed: string;
  barriers: string;
  continued_justification: string;
  community_referrals: string;
  warnings?: string[];
  source: "azure_openai" | "rule_based";
  notice: string;
}

/** AI draft of a monthly summary's narrative (not saved). AI drafts can take up to ~90s. */
export function draftPrpMonthlySummary(id: string): Promise<PrpMonthlyAIDraft> {
  return apiPostRaw(`/prp-documents/${id}/ai-draft`, undefined, { timeout: 100_000 });
}

export interface PrpContactNoteAIDraft extends Partial<
  Omit<PrpContactNoteContent, "goals" | "irp_id">
> {
  goals?: PrpContactNoteGoal[];
  warnings?: string[];
  source: "azure_openai" | "rule_based";
  notice: string;
}

/** AI draft of a contact note from the counselor's quick notes (not saved). Empty fields = not stated. */
export function draftPrpContactNote(id: string, bullets: string): Promise<PrpContactNoteAIDraft> {
  return apiPostRaw(`/prp-documents/${id}/ai-draft`, { bullets }, { timeout: 100_000 });
}

/** One AI goal completion (goal_id set) or suggestion. Missing fields = nothing suggested. */
export interface PrpIrpGoalDraft {
  goal_id?: string;
  domain?: string;
  client_words?: string;
  clinical_goal?: string;
  target_date?: string;
  short_term_goal?: string;
  client_will?: string[];
  rc_will?: string[];
  why?: string;
  when?: string;
  basis?: string;
}

export interface PrpIrpAIDraft {
  completions: PrpIrpGoalDraft[];
  new_goals: PrpIrpGoalDraft[];
  warnings: string[];
  sources: string[];
  source: "azure_openai" | "rule_based";
  notice: string;
}

/** AI goal suggestions for a draft IRP from the latest DLA-20 and Presenting Problems (not saved). */
export function draftPrpIrpGoals(id: string, focus: string): Promise<PrpIrpAIDraft> {
  return apiPostRaw(`/prp-documents/${id}/ai-draft`, { focus }, { timeout: 100_000 });
}

export interface PrpIrpReviewGoal {
  goal_id: string;
  label: string;
  contacts: number;
  ratings: { date: string; progress: number; text?: string }[];
  already_reviewed: boolean;
  /** Suggestions - missing when the AI had none. */
  status?: "met" | "continue" | "revised" | "discontinued";
  review_assessment?: string;
  intervention_changed?: boolean;
  revised_short_term_goal?: string;
}

export interface PrpIrpReviewDraft {
  period_start: string;
  period_end: string;
  contacts: number;
  goals: PrpIrpReviewGoal[];
  warnings: string[];
  source: "azure_openai" | "rule_based" | "none";
  notice: string;
}

/** AI draft of an IRP review's carried goals, with the period's evidence (not saved). */
export function draftPrpIrpReview(id: string): Promise<PrpIrpReviewDraft> {
  return apiPostRaw(`/prp-documents/${id}/ai-draft`, { review: true }, { timeout: 100_000 });
}

export interface PrpAuditFinding {
  severity: "high" | "medium" | "low";
  area: string;
  issue: string;
  fix: string;
}

export interface PrpAuditResult {
  summary: string;
  findings: PrpAuditFinding[];
  source: "azure_openai" | "unavailable";
}

/** AI pre-sign check of a saved document - advisory only. */
export function auditPrpDocument(id: string): Promise<PrpAuditResult> {
  return apiPostRaw(`/prp-documents/${id}/ai-audit`, undefined, { timeout: 100_000 });
}

export interface PrpRenewalLetter {
  letter: string;
  warnings: string[];
  source: "azure_openai" | "rule_based";
}

/** AI draft of a continued-stay request for an authorization (not saved). */
export function draftPrpRenewalLetter(authId: string): Promise<PrpRenewalLetter> {
  return apiPostRaw(`/prp-authorizations/${authId}/renewal-letter`, undefined, {
    timeout: 100_000,
  });
}

/** Voice clip (WAV) to text via the server's speech service. */
export function transcribePrpAudio(wav: Blob): Promise<{ text: string }> {
  return apiPostRaw("/prp/transcribe", wav, { headers: { "Content-Type": "audio/wav" } });
}

export function getPrpMonthlyRollup(patientId: string, month: string): Promise<PrpMonthlyRollup> {
  return apiGet("/prp/monthly-rollup", { params: { patient_id: patientId, month } });
}

export function getPrpWorkflows(): Promise<Record<PrpDocType, PrpWorkflowStep[]>> {
  return apiGet("/prp/workflows");
}

export function getPrpCaseload(mine = false): Promise<PrpCaseloadEntry[]> {
  return apiGet("/prp/caseload", { params: mine ? { mine: true } : undefined });
}

export function getPrpWorkQueue(mine = false): Promise<PrpWorkQueue> {
  return apiGet("/prp/work-queue", { params: mine ? { mine: true } : undefined });
}

export async function listPrpDocuments(
  filter: PrpDocumentFilter = {},
): Promise<{ data: PrpDocument[]; total: number }> {
  if (filter.awaiting_me) {
    const all = await getPrpAwaitingMe();
    const start = filter.offset ?? 0;
    return { data: all.data.slice(start, start + (filter.limit ?? 50)), total: all.total };
  }
  return apiGetRaw("/prp-documents", { params: filter });
}

/** Documents waiting on the logged-in user's signature, oldest first. */
export function getPrpAwaitingMe(): Promise<{ data: PrpDocument[]; total: number }> {
  return apiGetRaw("/prp/awaiting-me");
}

export function getPrpDocument(id: string): Promise<PrpDocument> {
  return apiGetRaw(`/prp-documents/${id}`);
}

export function createPrpDocument(req: CreatePrpDocumentRequest): Promise<PrpDocument> {
  return apiPostRaw("/prp-documents", req);
}

export function updatePrpDocument(
  id: string,
  req: { content: Record<string, unknown>; version: number; document_date?: string },
): Promise<PrpDocument> {
  return apiPutRaw(`/prp-documents/${id}`, req);
}

export function signPrpDocument(id: string, req: SignPrpDocumentRequest): Promise<PrpDocument> {
  return apiPostRaw(`/prp-documents/${id}/sign`, req);
}

export function requestPrpSignatures(id: string): Promise<PrpDocument> {
  return apiPostRaw(`/prp-documents/${id}/request-signatures`);
}

/** Validate a draft and lock it for signatures (nothing is sent). */
export function finishPrpDocument(id: string): Promise<PrpDocument> {
  return apiPostRaw(`/prp-documents/${id}/finish`);
}

/** Re-read the client block on a draft referral from the chart. */
export function refreshPrpReferralClient(id: string): Promise<PrpDocument> {
  return apiPostRaw(`/prp-documents/${id}/refresh-client`);
}

/** Month's billing worksheet (CSV, PHI) - Directors / admins. */
export async function downloadPrpBillingWorksheet(month: string): Promise<void> {
  const r = await apiClient.get<Blob>("/prp/billing-worksheet", { params: { month }, responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `prp-billing-worksheet-${month}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Printable PDF of a referral. */
export async function getPrpDocumentPdf(id: string): Promise<Blob> {
  const r = await apiClient.get<Blob>(`/prp-documents/${id}/pdf`, { responseType: "blob" });
  return r.data;
}

export type PrpLinkChannel = "email" | "sms";

/** Documents whose client signature can be sent as a link. */
export const PRP_CLIENT_LINK_DOCS = new Set<string>(["contact_note", "irp", "presenting_problems"]);

/** Documents a parent / guardian signs (PRP-M) - by link or in person. */
export const PRP_GUARDIAN_LINK_DOCS = new Set<string>(["irp", "presenting_problems"]);

export type PrpSignLinkStatus = "active" | "opened" | "verified" | "signed" | "expired" | "revoked";

export interface PrpSignLink {
  id: string;
  step_key: string;
  recipient: string;
  channel: PrpLinkChannel;
  /** Masked email or phone the link went to. */
  destination_hint: string;
  status: PrpSignLinkStatus;
  created_at: string;
  expires_at: string;
  opened_at?: string;
  verified_at?: string;
  used_at?: string;
  revoked_at?: string;
}

export function listPrpSignLinks(id: string): Promise<{ data: PrpSignLink[] }> {
  return apiGetRaw(`/prp-documents/${id}/sign-links`);
}

/**
 * Send a secure signing link. No body: the referral's outside provider by
 * email. `{ step: "client", channel }`: the client, by text or email.
 */
export function sendPrpSignLink(
  id: string,
  body?:
    | { step: "client"; channel: PrpLinkChannel }
    | { step: "psychiatrist" }
    | {
        step: "guardian";
        channel: PrpLinkChannel;
        /** Blank: the client's emergency contact, if listed as parent / guardian. */
        guardian_name?: string;
        guardian_phone?: string;
        guardian_email?: string;
      },
): Promise<{ link: PrpSignLink; url: string; sent: boolean }> {
  return apiPostRaw(`/prp-documents/${id}/sign-links`, body);
}

export function revokePrpSignLink(id: string, linkId: string): Promise<{ revoked: boolean }> {
  return apiPostRaw(`/prp-documents/${id}/sign-links/${linkId}/revoke`);
}

export function returnPrpDocument(id: string, reason: string): Promise<PrpDocument> {
  return apiPostRaw(`/prp-documents/${id}/return`, { reason });
}

export function voidPrpDocument(id: string, reason: string): Promise<PrpDocument> {
  return apiPostRaw(`/prp-documents/${id}/void`, { reason });
}

export function addPrpAddendum(id: string, body: string): Promise<PrpDocument> {
  return apiPostRaw(`/prp-documents/${id}/addenda`, { body });
}

export function listPrpEnrollments(patientId?: string): Promise<PrpEnrollment[]> {
  return apiGet("/prp/enrollments", { params: patientId ? { patient_id: patientId } : undefined });
}

export function enrollPrpClient(req: {
  patient_id: string;
  referral_document_id: string;
  counselor_user_id: string;
  admission_date: string;
  program?: "adult" | "minor";
  service_level?: PrpServiceLevel;
}): Promise<PrpEnrollment> {
  return apiPostRaw("/prp/enrollments", req);
}

export function updatePrpEnrollment(
  id: string,
  req: { counselor_user_id?: string; program?: "adult" | "minor"; service_level?: PrpServiceLevel },
): Promise<PrpEnrollment> {
  return apiPutRaw(`/prp-enrollments/${id}`, req);
}

export function dischargePrpEnrollment(
  id: string,
  req: { discharge_date: string; reason: string },
): Promise<PrpEnrollment> {
  return apiPostRaw(`/prp-enrollments/${id}/discharge`, req);
}

export function listPrpAuthorizations(
  filter: {
    patient_id?: string;
    enrollment_id?: string;
    expiring_within_days?: number;
  } = {},
): Promise<PrpAuthorization[]> {
  return apiGet("/prp/authorizations", { params: filter });
}

export function createPrpAuthorization(req: PrpAuthorizationRequest): Promise<PrpAuthorization> {
  return apiPostRaw("/prp/authorizations", req);
}

export function updatePrpAuthorization(
  id: string,
  req: PrpAuthorizationRequest,
): Promise<PrpAuthorization> {
  return apiPutRaw(`/prp-authorizations/${id}`, req);
}

// ── Referral intake queue ───────────────────────────────────────────────────

export type PrpIntakeStatus = "received" | "screening" | "accepted" | "denied" | "withdrawn";
export type PrpIntakeSource = "fax" | "email" | "phone" | "mail" | "walk_in" | "other";

export const PRP_INTAKE_SOURCES: { key: PrpIntakeSource; label: string }[] = [
  { key: "fax", label: "Fax" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "mail", label: "Mail" },
  { key: "walk_in", label: "Walk-in" },
  { key: "other", label: "Other" },
];

export const PRP_INTAKE_STATUS_LABELS: Record<PrpIntakeStatus, string> = {
  received: "New",
  screening: "Screening",
  accepted: "Accepted",
  denied: "Denied",
  withdrawn: "Withdrawn",
};

export interface PrpIntake {
  id: string;
  status: PrpIntakeStatus;
  source: PrpIntakeSource;
  received_date: string;
  client: {
    first_name: string;
    last_name: string;
    date_of_birth: string;
    gender: string;
    phone: string;
    medicaid_number: string;
  };
  referrer: { name: string; agency: string; email: string; phone: string; fax: string };
  notes: string;
  patient_id?: string;
  referral_document_id?: string;
  referral_status?: PrpDocStatus;
  /** Copy of the attached file in the client's chart documents. */
  chart_document_id?: string;
  file?: { name: string; content_type: string; size: number };
  decision_reason?: string;
  decided_by?: string;
  decided_at?: string;
  created_by: string;
  created_at: string;
}

/** What staff type when a referral comes in (also used to edit). */
export interface PrpIntakeInput {
  source: PrpIntakeSource;
  received_date: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender: string;
  phone: string;
  medicaid_number: string;
  referrer_name: string;
  referrer_agency: string;
  referrer_email: string;
  referrer_phone: string;
  referrer_fax: string;
  notes: string;
}

export function listPrpIntakes(status: "open" | "all" | PrpIntakeStatus = "open"): Promise<{ data: PrpIntake[] }> {
  return apiGetRaw(`/prp/intakes`, { params: { status } });
}

export function getPrpIntake(id: string): Promise<PrpIntake> {
  return apiGetRaw(`/prp-intakes/${id}`);
}

/** Log an incoming referral, optionally with the faxed / emailed file. */
export async function createPrpIntake(input: PrpIntakeInput, file?: File | null): Promise<PrpIntake> {
  if (!file) return apiPostRaw(`/prp/intakes`, input);
  const form = new FormData();
  for (const [k, v] of Object.entries(input)) form.append(k, v ?? "");
  form.append("file", file);
  const res = await apiClient.post<PrpIntake>(`/prp/intakes`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export function updatePrpIntake(id: string, input: PrpIntakeInput): Promise<PrpIntake> {
  return apiPutRaw(`/prp-intakes/${id}`, input);
}

export function matchPrpIntake(id: string, patientId: string): Promise<PrpIntake> {
  return apiPostRaw(`/prp-intakes/${id}/match`, { patient_id: patientId });
}

export function createPrpIntakeClient(id: string): Promise<PrpIntake> {
  return apiPostRaw(`/prp-intakes/${id}/create-client`);
}

export function startPrpIntakeReferral(id: string): Promise<PrpIntake> {
  return apiPostRaw(`/prp-intakes/${id}/start-referral`);
}

export function savePrpIntakeToChart(id: string): Promise<PrpIntake> {
  return apiPostRaw(`/prp-intakes/${id}/save-to-chart`);
}

export function decidePrpIntake(id: string, status: "denied" | "withdrawn", reason: string): Promise<PrpIntake> {
  return apiPostRaw(`/prp-intakes/${id}/decide`, { status, reason });
}

export async function getPrpIntakeFile(id: string): Promise<Blob> {
  const r = await apiClient.get<Blob>(`/prp-intakes/${id}/file`, { responseType: "blob" });
  return r.data;
}

export async function getPrpIntakeLetter(id: string): Promise<Blob> {
  const r = await apiClient.get<Blob>(`/prp-intakes/${id}/letter`, { responseType: "blob" });
  return r.data;
}
